import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { getApiError } from 'src/utils/errors';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormField, FormItem, FormControl, FormMessage, FormLabel, FormOptional } from '@/components/ui/form';

import { declineRequestSchema, type IDeclineRequestForm } from '../model/declineRequestSchema';

type Props = {
  requestId: string | null;
  companyName: string;
  onOpenChange: (open: boolean) => void;
  onSubmit: (reason: string) => Promise<void>;
};

export const DeclineRequestDialog = ({ requestId, companyName, onOpenChange, onSubmit }: Props) => {
  const form = useForm<IDeclineRequestForm>({
    resolver: zodResolver(declineRequestSchema),
    defaultValues: { reason: '' },
  });

  const handleFormSubmit = async ({ reason }: IDeclineRequestForm) => {
    try {
      await onSubmit(reason ?? '');
      toast.success('Request declined');
      onOpenChange(false);
      form.reset();
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <AlertDialog open={requestId !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Decline {companyName}&apos;s request</AlertDialogTitle>
          <AlertDialogDescription>
            {companyName} will be notified. You can add a reason if you&apos;d like them to know why.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)}>
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem className="mb-6">
                  <div className="flex items-center justify-between gap-2">
                    <FormLabel className="mb-0">Reason</FormLabel>
                    <FormOptional />
                  </div>
                  <FormControl>
                    <Textarea
                      placeholder="e.g. We already have escrow in place for this application"
                      {...field}
                      className="wrap-anywhere"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AlertDialogFooter>
              <AlertDialogCancel type="button" onClick={() => onOpenChange(false)}>
                Cancel
              </AlertDialogCancel>
              <Button
                type="submit"
                variant="destructive"
                disabled={form.formState.isSubmitting}
                isLoading={form.formState.isSubmitting}
              >
                Decline request
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
